import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMapEvents } from 'react-leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icon issue
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
}

export default function GeofenceManagement() {
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newGeofence, setNewGeofence] = useState({
    name: '',
    latitude: 51.1657,
    longitude: 10.4515,
    radius: 1000
  });
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    fetchGeofences();
  }, []);

  const fetchGeofences = async () => {
    try {
      const res = await axios.get('/geofences');
      setGeofences(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Geofences');
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = (latlng) => {
    setSelectedLocation(latlng);
    setNewGeofence(prev => ({
      ...prev,
      latitude: latlng.lat,
      longitude: latlng.lng
    }));
    toast.info('Standort ausgewählt! Geben Sie einen Namen ein.');
  };

  const handleCreateGeofence = async (e) => {
    e.preventDefault();
    
    if (!newGeofence.name) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      await axios.post('/geofences', newGeofence);
      toast.success('Geofence erfolgreich erstellt');
      setShowForm(false);
      setNewGeofence({
        name: '',
        latitude: 51.1657,
        longitude: 10.4515,
        radius: 1000
      });
      setSelectedLocation(null);
      fetchGeofences();
    } catch (err) {
      toast.error('Fehler beim Erstellen des Geofence');
    }
  };

  const handleDeleteGeofence = async (geofenceId) => {
    if (!window.confirm('Möchten Sie diesen Geofence wirklich löschen?')) return;

    try {
      await axios.delete(`/geofences/${geofenceId}`);
      toast.success('Geofence gelöscht');
      fetchGeofences();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-foreground">Geofencing</h1>
          <p className="text-muted-foreground mt-2">
            Definieren Sie geografische Grenzen für Fahrzeugüberwachung
          </p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} size="lg">
          <Plus className="mr-2 h-5 w-5" />
          {showForm ? 'Abbrechen' : 'Neuer Geofence'}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle>Neuen Geofence erstellen</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateGeofence} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newGeofence.name}
                  onChange={(e) => setNewGeofence(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Hauptfiliale"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Breitengrad</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={newGeofence.latitude}
                    onChange={(e) => setNewGeofence(prev => ({ ...prev, latitude: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Längengrad</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={newGeofence.longitude}
                    onChange={(e) => setNewGeofence(prev => ({ ...prev, longitude: parseFloat(e.target.value) }))}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="radius">Radius (Meter)</Label>
                <Input
                  id="radius"
                  type="number"
                  value={newGeofence.radius}
                  onChange={(e) => setNewGeofence(prev => ({ ...prev, radius: parseInt(e.target.value) }))}
                  min="100"
                  required
                />
              </div>

              <p className="text-sm text-muted-foreground">
                💡 Klicken Sie auf die Karte unten, um einen Standort auszuwählen
              </p>

              <Button type="submit" className="w-full">
                <MapPin className="mr-2 h-4 w-4" />
                Geofence erstellen
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Geofence List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Aktive Geofences ({geofences.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {geofences.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Keine Geofences definiert
              </p>
            ) : (
              <div className="space-y-3">
                {geofences.map((geofence) => (
                  <div
                    key={geofence.geofence_id}
                    className="flex items-center justify-between p-4 bg-accent rounded-lg"
                  >
                    <div>
                      <h4 className="font-semibold">{geofence.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Radius: {geofence.radius}m
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteGeofence(geofence.geofence_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Karte</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[600px] rounded-lg overflow-hidden">
              <MapContainer
                center={[51.1657, 10.4515]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                <MapClickHandler onMapClick={handleMapClick} />

                {/* Existing Geofences */}
                {geofences.map((geofence) => (
                  <Circle
                    key={geofence.geofence_id}
                    center={[geofence.latitude, geofence.longitude]}
                    radius={geofence.radius}
                    pathOptions={{
                      color: geofence.active ? '#dc2626' : '#94a3b8',
                      fillColor: geofence.active ? '#dc2626' : '#94a3b8',
                      fillOpacity: 0.2
                    }}
                  >
                    <Popup>
                      <div className="text-center">
                        <strong>{geofence.name}</strong><br />
                        Radius: {geofence.radius}m
                      </div>
                    </Popup>
                  </Circle>
                ))}

                {/* Selected Location Preview */}
                {selectedLocation && (
                  <>
                    <Marker position={[selectedLocation.lat, selectedLocation.lng]}>
                      <Popup>Neuer Geofence</Popup>
                    </Marker>
                    <Circle
                      center={[selectedLocation.lat, selectedLocation.lng]}
                      radius={newGeofence.radius}
                      pathOptions={{
                        color: '#22c55e',
                        fillColor: '#22c55e',
                        fillOpacity: 0.2
                      }}
                    />
                  </>
                )}
              </MapContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
